import React from 'react';

import loadable from '@loadable/component';
import { observer } from 'mobx-react-lite';

import { getCurrentFreshness } from '^/types/__ResultType';
import { extractTokenData } from '^/utils/token/forClient';

import { AdminHeaderTabs } from './Components/admin/HeaderTabs/HeaderTabs';
import { WithTabBar } from './Components/Tabbar/WithTabBar';
import { CowMountHistoryPage } from './Pages/CowMountHistoryPage/CowMountHistoryPage';
import {
  CowMountHistoryPageViewModel,
} from './Pages/CowMountHistoryPage/CowMountHistoryPage.viewmodel';
import { DetectedVisitorsPage } from './Pages/DetectedVisitorsPage/DetectedVisitorsPage';
import {
  DetectedVisitorsPageViewModel,
} from './Pages/DetectedVisitorsPage/DetectedVisitorsPage.viewmodel';
import { EmptyPage } from './Pages/EmptyPage';
import { LoginPageViewModel } from './Pages/LoginPage/LoginPage.viewmodel';
import { MainPage } from './Pages/MainPage/MainPage';
import { MainPageViewModel } from './Pages/MainPage/MainPage.viewmodel';
import { MyPage } from './Pages/MyPage/MyPage';
import { MyPageViewModel } from './Pages/MyPage/MyPage.viewmodel';
import { MySettingsPage } from './Pages/MySettingsPage/MySettingsPage';
import { MySettingsPageViewModel } from './Pages/MySettingsPage/MySettingsPage.viewmodel';
import { NotificationPage } from './Pages/NotificationPage/NotificationsPage';
import { NotificationsPageViewModel } from './Pages/NotificationPage/NotificationsPage.viewmodel';
import { SensorsPage } from './Pages/SensorsPage/SensorsPage';
import { SensorsPageViewModel } from './Pages/SensorsPage/SensorsPage.viewmodel';
import { StatsPage } from './Pages/StatsPage/StatsPage';
import { StatsPageViewModel } from './Pages/StatsPage/StatsPage.viewmodel';
import {
  router, routes,
} from './routes';
import type { AppContext } from './types/AppContext';

const LoginPage = loadable(() => import(
  './Pages/LoginPage/LoginPage'
));

const AdminRoutesPageRenderer = loadable(() => import(
  './routesPageRenderer.Admin'
));

export const RoutesPageRenderer: React.FC<{
  appContext: AppContext,
}> = observer(function RoutePageRenderer({
  appContext,
}) {
  const route = router.useRoute();
  if (route.name === 'login') {
    const viewmodel = new LoginPageViewModel(appContext);
    return <LoginPage viewmodel={viewmodel} />;
  }
  if (appContext.tokenRepo.userToken.status !== 'success') {
    setTimeout(() => routes.login()
      .replace());
    return null;
  }

  const tokenData = extractTokenData(
    appContext.tokenRepo.userToken.value,
  );
  const isAdmin = tokenData.userKind === 'admin';

  if (route.name === 'main') {
    const viewModel = new MainPageViewModel(appContext);

    return (

      <WithTabBar
        routeName="main"
      >
        <AdminHeaderTabs routeName={route.name} isAdmin={isAdmin} />
        <MainPage viewmodel={viewModel} />

      </WithTabBar>
    );
  }
  if (route.name === 'myPage') {
    const viewmodel = new MyPageViewModel(appContext);
    return (

      <WithTabBar
        routeName="myPage"
      >
        <AdminHeaderTabs routeName={route.name} isAdmin={isAdmin} />
        <MyPage viewmodel={viewmodel} />
      </WithTabBar>

    );
  }

  if (route.name === 'notification') {
    const viewmodel = new NotificationsPageViewModel(appContext);

    return (
      <NotificationPage
        viewmodel={viewmodel}
      />
    );
  }

  if (route.name === 'notificationSetting') {
    const viewmodel = new MySettingsPageViewModel(
      appContext,
    );
    return (
      <MySettingsPage
        viewmodel={viewmodel}
      />
    );
  }

  if (route.name === 'visitorHistory') {
    const viewmodel = new DetectedVisitorsPageViewModel(
      appContext,
      undefined,
      undefined,
      route.params.fullscreenNth,
    );
    return (

      <WithTabBar
        routeName={route.name}

      >
        <AdminHeaderTabs routeName={route.name} isAdmin={isAdmin} />
        <DetectedVisitorsPage
          title="최근 방문한 기록"
          viewmodel={viewmodel}
        />
      </WithTabBar>

    );
  }

  if (route.name === 'cowMountingHistory') {
    const detectionType = route.params.detectionType || 'ride';
    const viewmodel = new CowMountHistoryPageViewModel(
      appContext,
      undefined,
      undefined,
      detectionType,
      route.params.fullscreenNth,
      undefined,
      undefined,
      route.params.freshness,
    );

    const title = detectionType === 'ride' ? '최근 발생한 승가' : '최근 발생한 스탄존';
    return (

      <WithTabBar
        routeName={route.name}

      >
        <AdminHeaderTabs routeName={route.name} isAdmin={isAdmin} />
        <CowMountHistoryPage
          title={title}
          viewmodel={viewmodel}
        />
      </WithTabBar>

    );
  }
  if (route.name === 'sensors') {
    if (!route.params.freshness) {
      setTimeout(() => {
        routes.sensors({
          ...route.params,
          freshness: getCurrentFreshness(),
        }).replace();
      }, 20);
      return null;
    }
    const viewmodel = new SensorsPageViewModel(
      appContext,
      route.params.freshness,
      route.params.selectedDevice ?? null,
    );
    return (
      <WithTabBar
        routeName={route.name}
      >
        <AdminHeaderTabs routeName={route.name} isAdmin={isAdmin} />
        <SensorsPage
          viewmodel={viewmodel}
        />
      </WithTabBar>

    );
  }
  if (route.name === 'stats') {
    if (!route.params.freshness) {
      setTimeout(() => {
        routes.stats({
          ...route.params,
          freshness: getCurrentFreshness(),
        }).replace();
      }, 20);
      return null;
    }
    const viewmodel = new StatsPageViewModel(
      appContext,
      route.params.freshness,
    );
    return (
      <WithTabBar
        routeName={route.name}
      >
        <AdminHeaderTabs routeName={route.name} isAdmin={isAdmin} />
        <StatsPage
          viewmodel={viewmodel}
        />
      </WithTabBar>

    );
  }

  if (isAdmin) {
    return (
      <AdminRoutesPageRenderer
        appContext={appContext}
      />
    )
  }

  // if (route.name === 'adminIotPage') {
  //   if (!route.params.freshness) {
  //     setTimeout(() => {
  //       routes.adminIotPage({
  //         ...route.params,
  //         freshness: getCurrentFreshness(),
  //       }).replace();
  //     }, 50);
  //     return null;
  //   }
  //   const viewmodel = new AdminIotDevicePageViewModel(
  //     appContext,
  //   );
  //   return (
  //     <WithTabBar
  //       routeName={route.name}
  //     >
  //       <AdminHeaderTabs routeName={route.name} isAdmin={isAdmin} />
  //       <AdminIotDevicePage
  //         viewmodel={viewmodel}
  //       />

  //     </WithTabBar>
  //   );
  // }
  // if (route.name === 'adminCamerasPage') {
  //   if (!route.params.freshness) {
  //     setTimeout(() => {
  //       routes.adminCamerasPage({
  //         ...route.params,
  //         freshness: getCurrentFreshness(),
  //       }).replace();
  //     }, 50);
  //     return null;
  //   }
  //   const viewmodel = new AdminCamerasPageViewmodel(
  //     appContext,
  //     route.params.freshness,
  //   );
  //   return (
  //     <WithTabBar
  //       routeName={route.name}
  //     >
  //       <AdminHeaderTabs routeName={route.name} isAdmin={isAdmin} />
  //       <AdminCamerasPage
  //         viewmodel={viewmodel}
  //       />

  //     </WithTabBar>
  //   );
  // }
  // if (route.name === 'adminUsersPage') {
  //   if (!route.params.freshness) {
  //     setTimeout(() => {
  //       routes.adminUsersPage({
  //         ...route.params,
  //         freshness: getCurrentFreshness(),
  //       }).replace();
  //     }, 50);
  //     return null;
  //   }

  //   const viewmodel = new AdminUsersPageViewModel(
  //     appContext,
  //     route.params.freshness
  //   );

  //   return (
  //     <WithTabBar
  //       routeName={route.name}
  //     >
  //       <AdminHeaderTabs routeName={route.name} isAdmin={isAdmin} />
  //       <AdminUsersPage
  //         viewmodel={viewmodel}
  //       />

  //     </WithTabBar>
  //   );
  // }

  return (
    <WithTabBar
      routeName={route.name}
    >
      <EmptyPage
        pageName={route.name}
      />
    </WithTabBar>
  );
});
